// Mock data simulating what would come from Cosmos DB

const seedWorkflows = [
  {
    id: 'wf-001',
    name: 'Client Onboarding — Acme Corp',
    description: 'End-to-end client onboarding process including document collection, compliance verification, account setup, and welcome package delivery.',
    type: 'Onboarding',
    status: 'running',
    currentStep: 3,
    totalSteps: 6,
    startedAt: '2026-03-30T09:15:00Z',
    updatedAt: '2026-03-30T10:42:00Z',
    humanBaseline: 18,
    grossValue: 700,
    totalTokens: 145200,
    agentTokens: 98400,
    hostTokens: 46800,
    humanInterventions: 1,
    agents: [
      { id: 'a1', name: 'Document Collector', status: 'completed', description: 'Retrieves and validates required documents from client intake forms, shared drives, and email attachments. Checks file formats, completeness, and expiration dates.' },
      { id: 'a2', name: 'Compliance Checker', status: 'completed', description: 'Cross-references business credentials against government registries (state, IRS, insurance boards). Generates compliance reports with pass/fail determinations.' },
      { id: 'a3', name: 'Account Provisioner', status: 'running', description: 'Creates client workspaces, configures billing profiles, sets up permission groups, and allocates resources based on selected service tier.' },
      { id: 'a4', name: 'Welcome Agent', description: 'Generates personalized welcome packages including branded materials, integration guides, and schedules the initial kickoff call with the account team.' },
    ],
    steps: [
      {
        id: 's1',
        name: 'Collect client documents',
        status: 'completed',
        agentId: 'a1',
        agentName: 'Document Collector',
        startedAt: '2026-03-30T09:15:00Z',
        completedAt: '2026-03-30T09:28:00Z',
        action: 'Scanned the client intake form and retrieved 4 required documents from the shared drive: business license, tax ID certificate, proof of insurance, and signed service agreement.',
        delivered: 'A verified document package with all 4 required files validated for completeness and correct formatting.',
        reasoning: 'The onboarding checklist requires these 4 documents before compliance review can begin. All documents were found in the expected location and passed format validation.',
        activityLog: [
          { time: '09:15:02', type: 'thinking', text: 'I need to collect the required onboarding documents for Acme Corp. Let me start by pulling their onboarding checklist to see exactly what\'s needed before I start searching.' },
          { time: '09:15:08', type: 'tool', tool: 'read_file', text: 'Reading templates/onboarding-checklist.json', result: 'Found checklist with 4 required documents:\n1. Business license (must be current)\n2. Tax ID certificate\n3. Proof of insurance (min $1M coverage)\n4. Signed service agreement' },
          { time: '09:15:22', type: 'thinking', text: 'Got the checklist. 4 documents required. The insurance one has a minimum coverage threshold of $1M — I\'ll need to actually parse the policy to verify that, not just check the file exists. Let me scan their intake folder.' },
          { time: '09:16:30', type: 'tool', tool: 'list_directory', text: 'Scanning /clients/acme-corp/intake/', result: '6 files found:\n• business_license_2026.pdf (2.1 MB)\n• tax_id_cert.pdf (340 KB)\n• insurance_policy_2026.pdf (1.8 MB)\n• service_agreement_signed.pdf (890 KB)\n• cover_letter.pdf (120 KB)\n• company_overview.pptx (4.2 MB)' },
          { time: '09:17:12', type: 'thinking', text: '6 files in the folder but I only need 4. The cover_letter.pdf and company_overview.pptx aren\'t on the checklist — I\'ll ignore those. Let me validate each required document one at a time, starting with the business license since it has an expiration date I need to check.' },
          { time: '09:17:45', type: 'tool', tool: 'parse_document', text: 'Parsing business_license_2026.pdf for license number and expiration', result: 'License #BL-4492817\nRegistered to: Acme Corp LLC\nIssued: 2024-06-15\nExpires: 2027-06-15\nStatus: Active\nState: California' },
          { time: '09:18:05', type: 'thinking', text: 'License is active and doesn\'t expire until June 2027 — well within the validity window. That\'s document 1 of 4 confirmed. ✓' },
          { time: '09:18:20', type: 'tool', tool: 'parse_document', text: 'Parsing tax_id_cert.pdf for TIN verification', result: 'TIN: ***-**-4821\nEntity: Acme Corp LLC\nType: EIN (Employer Identification Number)\nFormat: Valid federal EIN pattern' },
          { time: '09:18:40', type: 'thinking', text: 'Tax ID checks out — valid EIN format and entity name matches. Document 2 of 4 confirmed. ✓ Now for the insurance policy, which is the tricky one since I need to verify the coverage amount meets our $1M minimum.' },
          { time: '09:19:55', type: 'tool', tool: 'parse_document', text: 'Parsing insurance_policy_2026.pdf — extracting coverage details', result: 'Policy #INS-20260115\nCarrier: MutualGuard Insurance Co.\nType: Commercial General Liability\nCoverage: $1,400,000\nDeductible: $5,000\nEffective: 2026-01-15 to 2027-01-15\nNamed insured: Acme Corp LLC' },
          { time: '09:21:10', type: 'thinking', text: 'Insurance coverage is $1.4M against our $1M minimum — that\'s a 40% buffer. Good sign for their risk profile. Policy is current and won\'t expire until January 2027. Document 3 of 4 confirmed. ✓' },
          { time: '09:23:00', type: 'tool', tool: 'verify_signature', text: 'Verifying digital signature on service_agreement_signed.pdf', result: 'Digital signature verified ✓\nSigner: J. Chen (CEO, Acme Corp)\nSigned: 2026-03-28T14:22:00Z\nCertificate: Valid, issued by DocuSign\nDocument integrity: Unmodified since signing' },
          { time: '09:23:30', type: 'thinking', text: 'Service agreement has a valid digital signature from their CEO, signed 2 days ago. Document hasn\'t been tampered with. That\'s all 4 documents validated. ✓\n\nI\'m confident this package is complete. The two extra files in the folder aren\'t required and I don\'t need to flag them — they\'re just supplementary materials the client included.' },
          { time: '09:27:00', type: 'result', text: 'Document package assembled and validated. All 4 required files passed verification:\n• Business license — Active through 2027-06\n• Tax ID — Valid EIN confirmed\n• Insurance — $1.4M coverage (exceeds $1M minimum)\n• Service agreement — Digitally signed and verified\n\nReady for handoff to Compliance Checker.' },
        ],
      },
      {
        id: 's2',
        name: 'Run compliance verification',
        status: 'completed',
        agentId: 'a2',
        agentName: 'Compliance Checker',
        startedAt: '2026-03-30T09:28:00Z',
        completedAt: '2026-03-30T09:55:00Z',
        action: 'Cross-referenced business license number against the state registry, verified tax ID with IRS database, and checked insurance coverage meets minimum thresholds.',
        delivered: 'Compliance report showing all 4 checks passed. Business license is valid through 2027-06. Insurance coverage exceeds minimum by 40%.',
        reasoning: 'Regulatory requirements mandate verification of all business credentials before account creation. The insurance overage was flagged as a positive indicator for risk assessment.',
        activityLog: [
          { time: '09:28:05', type: 'thinking', text: 'Received the verified document package from Document Collector. My job is to cross-reference each credential against the actual external registries — state database, IRS, and insurance board. The Document Collector already parsed the files, but I need to confirm the data matches what the government has on record.' },
          { time: '09:28:30', type: 'tool', tool: 'api_call', text: 'Connecting to California Secretary of State Business Registry API', result: 'Connection established. Rate limit: 100 req/hr. Current usage: 12/100.' },
          { time: '09:29:45', type: 'tool', tool: 'api_call', text: 'GET /business/lookup?license=BL-4492817', result: 'Match found:\nEntity: Acme Corp LLC\nStatus: Active\nFiled: 2019-03-22\nLicense expires: 2027-06-15\nAgent for service: J. Chen\nJurisdiction: California' },
          { time: '09:30:12', type: 'thinking', text: 'State registry confirms the license is active and all details match what was in the PDF. Entity name, license number, expiration — all consistent. Moving to IRS verification for the tax ID.' },
          { time: '09:32:00', type: 'tool', tool: 'api_call', text: 'POST /irs/tin-match — verifying EIN ending in 4821 against entity "Acme Corp LLC"', result: 'TIN Match Result: MATCH\nEntity name: Acme Corp LLC\nTIN status: Valid, active\nLast verified: 2026-03-30' },
          { time: '09:33:45', type: 'thinking', text: 'IRS confirms the TIN. Two down, two to go. Now I need to verify the insurance policy is actually active with MutualGuard — the PDF could be outdated or fabricated. Let me check directly with the carrier\'s verification portal.' },
          { time: '09:36:20', type: 'tool', tool: 'api_call', text: 'GET /mutualguard/verify?policy=INS-20260115&entity=Acme+Corp+LLC', result: 'Policy verified:\nStatus: Active\nType: Commercial General Liability\nCoverage limit: $1,400,000\nEffective: 2026-01-15 through 2027-01-15\nPremium status: Current (paid through 2026-07-15)' },
          { time: '09:38:00', type: 'thinking', text: 'Insurance is verified directly with the carrier. $1.4M coverage against our $1M requirement — 40% over minimum. Premium is paid ahead through July. This is a well-insured client, which is a positive risk signal I should include in my report.\n\nAll 4 external verifications passed. No discrepancies found anywhere. This is a clean compliance check.' },
          { time: '09:50:00', type: 'tool', tool: 'generate_report', text: 'Generating compliance verification report', result: 'Report generated: compliance_report_acme_corp_2026-03-30.pdf\nSections: Executive Summary, License Verification, Tax ID Verification, Insurance Verification, Risk Assessment\nOverall status: PASS\nRisk flags: 0 negative, 1 positive (insurance overage)' },
          { time: '09:54:30', type: 'result', text: 'Compliance verification complete. All 4 checks passed against external registries:\n• Business license — Confirmed active with CA Secretary of State\n• Tax ID — IRS TIN match confirmed\n• Insurance — Carrier-verified, $1.4M coverage (40% above minimum)\n• Risk assessment — Clean, with positive insurance indicator\n\nCompliance report generated. Clearing Acme Corp for account provisioning.' },
        ],
      },
      {
        id: 's3',
        name: 'Provision client account',
        status: 'running',
        agentId: 'a3',
        agentName: 'Account Provisioner',
        startedAt: '2026-03-30T09:55:00Z',
        completedAt: null,
        action: 'Creating client workspace, setting up billing profile, and configuring access permissions based on the selected service tier.',
        delivered: null,
        reasoning: 'Account provisioning follows compliance approval. The selected Enterprise tier requires additional permission groups and a dedicated resource allocation.',
        activityLog: [
          { time: '09:55:10', type: 'thinking', text: 'Compliance is cleared — I can proceed with account provisioning. Acme Corp selected the Enterprise tier, which means I need to set up a dedicated workspace, billing profile, 5 permission groups, and reserved compute resources. This is more involved than Standard tier.' },
          { time: '09:55:45', type: 'tool', tool: 'create_workspace', text: 'Creating tenant workspace for Acme Corp', result: 'Workspace created:\nSlug: acme-corp\nTenant ID: t-9f2a8b3c\nRegion: us-east-1\nTier: Enterprise\nStorage allocated: 500 GB' },
          { time: '09:57:20', type: 'tool', tool: 'configure_billing', text: 'Setting up billing profile with NET-30 payment terms', result: 'Billing profile created:\nPayment terms: NET-30\nBilling contact: ap@acmecorp.com\nInvoice frequency: Monthly\nCurrency: USD\nTax exempt: No' },
          { time: '10:02:00', type: 'thinking', text: 'Workspace and billing are set. Now I need the 5 Enterprise permission groups. Each one maps to a different set of capabilities — I\'ll use the Enterprise template but I should double-check the API access group since that has elevated privileges.' },
          { time: '10:04:15', type: 'tool', tool: 'create_permission_groups', text: 'Creating 5 Enterprise permission groups from template', result: 'Groups created:\n• admin (full access, user management)\n• manager (project management, reporting)\n• analyst (read access, data export)\n• viewer (read-only dashboard access)\n• api-access (programmatic access, rate-limited)' },
          { time: '10:08:30', type: 'tool', tool: 'allocate_resources', text: 'Requesting dedicated compute resources for Enterprise tier', result: 'Request submitted:\n• 4 vCPU reserved\n• 16 GB RAM allocated\n• Region: us-east-1\n• Status: PROVISIONING\n• Estimated ready: ~8 minutes' },
          { time: '10:15:00', type: 'thinking', text: 'Resource allocation is in progress. The cloud provisioner typically takes 5-10 minutes for dedicated instances in us-east-1. I\'m waiting for confirmation before I can proceed to integration configuration...' },
        ],
      },
      {
        id: 's4',
        name: 'Configure integrations',
        agentId: 'a3',
        agentName: 'Account Provisioner',
        startedAt: null,
        completedAt: null,
        action: null,
        delivered: null,
        reasoning: null,
      },
      {
        id: 's5',
        name: 'Generate welcome package',
        agentId: 'a4',
        agentName: 'Welcome Agent',
        startedAt: null,
        completedAt: null,
        action: null,
        delivered: null,
        reasoning: null,
      },
      {
        id: 's6',
        name: 'Send onboarding email',
        agentId: 'a4',
        agentName: 'Welcome Agent',
        startedAt: null,
        completedAt: null,
        action: null,
        delivered: null,
        reasoning: null,
      },
    ],
  },
  {
    id: 'wf-002',
    name: 'Invoice Processing — Q1 Batch',
    description: 'Automated processing of 47 vendor invoices including extraction, validation, approval routing, and payment scheduling.',
    type: 'Finance',
    status: 'completed',
    currentStep: 4,
    totalSteps: 4,
    startedAt: '2026-03-30T07:00:00Z',
    updatedAt: '2026-03-30T08:12:00Z',
    humanBaseline: 8,
    grossValue: 320,
    totalTokens: 67800,
    agentTokens: 52100,
    hostTokens: 15700,
    humanInterventions: 0,
    agents: [
      { id: 'a5', name: 'Invoice Extractor', status: 'completed', description: 'Uses OCR to parse PDF invoices, extracting vendor info, line items, totals, and due dates. Flags low-confidence extractions for manual review.' },
      { id: 'a6', name: 'Validation Agent', status: 'completed', description: 'Matches extracted invoice data against the purchase order database. Detects discrepancies in amounts, quantities, and vendor details.' },
      { id: 'a7', name: 'Approval Router', status: 'completed', description: 'Applies tiered approval rules based on invoice amount. Routes to appropriate approvers and schedules payments optimizing for early-pay discounts.' },
    ],
    steps: [
      {
        id: 's7',
        name: 'Extract invoice data',
        status: 'completed',
        agentId: 'a5',
        agentName: 'Invoice Extractor',
        startedAt: '2026-03-30T07:00:00Z',
        completedAt: '2026-03-30T07:18:00Z',
        action: 'Parsed 47 PDF invoices using OCR, extracted vendor name, invoice number, line items, totals, and due dates from each.',
        delivered: 'Structured data for 47 invoices. 45 extracted with high confidence (>95%). 2 flagged for manual review due to poor scan quality.',
        reasoning: 'Batch processing is more efficient than sequential. The 2 low-confidence extractions were flagged rather than guessed to prevent payment errors.',
        activityLog: [
          { time: '07:00:05', type: 'thinking', text: '47 PDF invoices to process for Q1. That\'s a decent batch — I\'ll parallelize the OCR to get through these quickly. Each worker can handle about 6 invoices. Let me spin up 8 workers to keep the throughput high.' },
          { time: '07:00:30', type: 'tool', tool: 'spawn_workers', text: 'Spawning 8 parallel OCR workers for batch processing', result: 'Workers spawned: 8\nDistribution: 6 invoices each (workers 1-7), 5 invoices (worker 8)\nOCR engine: Tesseract v5.3 with invoice-tuned model\nConfidence threshold: 95%' },
          { time: '07:04:15', type: 'tool', tool: 'batch_ocr', text: 'Workers 1-6 completed — processing results', result: '36 invoices extracted successfully\nAll above 95% confidence threshold\nAverage confidence: 98.2%\nFields extracted per invoice: vendor, invoice_number, date, line_items, subtotal, tax, total, due_date, payment_terms' },
          { time: '07:06:20', type: 'thinking', text: 'Worker 7 is flagging an issue. INV-2024-0392 was scanned at only 72 DPI — the text is blurry and the OCR confidence is just 61%. That\'s way below my 95% threshold. I\'d rather flag it for a human to re-scan than risk extracting wrong amounts. A payment error would be much worse than a short delay.' },
          { time: '07:07:10', type: 'tool', tool: 'batch_ocr', text: 'Worker 7 results — 1 flagged for manual review', result: '5 invoices extracted successfully (>95%)\n1 invoice flagged: INV-2024-0392\n  → Reason: Low scan quality (72 DPI)\n  → OCR confidence: 61%\n  → Recommendation: Re-scan at 300 DPI minimum' },
          { time: '07:08:45', type: 'tool', tool: 'batch_ocr', text: 'Worker 8 results — 1 flagged for manual review', result: '4 invoices extracted successfully (>95%)\n1 invoice flagged: INV-2024-0418\n  → Reason: Handwritten annotations overlapping printed text\n  → OCR confidence: 54%\n  → Affected fields: line_items, total\n  → Recommendation: Manual data entry required' },
          { time: '07:12:00', type: 'thinking', text: 'All workers done. 45 clean extractions, 2 flagged. The flagged ones have legitimate quality issues — not something I can work around with better prompting. Let me assemble the structured output for the 45 good ones and hand everything off to Validation Agent.' },
          { time: '07:15:30', type: 'tool', tool: 'structure_data', text: 'Assembling structured invoice dataset', result: 'Dataset created: q1_invoices_batch.json\n• 47 invoice records total\n• 45 status: ready_for_validation\n• 2 status: flagged_manual_review\n• Total value: $287,493.22\n• Date range: 2026-01-03 to 2026-03-28' },
          { time: '07:17:45', type: 'result', text: 'Batch OCR complete.\n\n45 invoices extracted with high confidence (avg 98.2%) — ready for PO validation.\n2 invoices flagged for manual review (poor scan quality).\nTotal batch value: $287,493.22\n\nHanding off to Validation Agent for PO matching.' },
        ],
      },
      {
        id: 's8',
        name: 'Validate against PO system',
        status: 'completed',
        agentId: 'a6',
        agentName: 'Validation Agent',
        startedAt: '2026-03-30T07:18:00Z',
        completedAt: '2026-03-30T07:35:00Z',
        action: 'Matched each invoice to its corresponding purchase order. Verified amounts, quantities, and vendor details against the PO database.',
        delivered: '44 invoices matched and validated. 3 invoices had discrepancies: 1 overcharge ($142 over PO amount), 2 with incorrect item quantities.',
        reasoning: 'PO matching is required before approval routing to prevent overpayment. Discrepancies are routed to the exceptions queue rather than auto-approved.',
      },
      {
        id: 's9',
        name: 'Route for approval',
        status: 'completed',
        agentId: 'a7',
        agentName: 'Approval Router',
        startedAt: '2026-03-30T07:35:00Z',
        completedAt: '2026-03-30T07:52:00Z',
        action: 'Applied approval rules: invoices under $5,000 auto-approved, $5,000-$25,000 routed to department manager, over $25,000 routed to VP Finance.',
        delivered: '38 invoices auto-approved, 5 routed to department managers, 1 routed to VP Finance. 3 exception invoices held for manual review.',
        reasoning: 'Approval thresholds follow company policy. Auto-approval for low-value invoices reduces bottlenecks while maintaining oversight for larger amounts.',
      },
      {
        id: 's10',
        name: 'Schedule payments',
        status: 'completed',
        agentId: 'a7',
        agentName: 'Approval Router',
        startedAt: '2026-03-30T07:52:00Z',
        completedAt: '2026-03-30T08:12:00Z',
        action: 'Scheduled approved invoices for payment based on vendor terms. Prioritized early-payment discounts where applicable.',
        delivered: 'Payment schedule created: 12 invoices scheduled for immediate payment (2% early discount captured), 26 scheduled at net-30, 6 pending approval.',
        reasoning: 'Early payment discounts of 2% across 12 invoices saves approximately $1,840. Remaining invoices optimized for cash flow at net-30 terms.',
      },
    ],
  },
  {
    id: 'wf-003',
    name: 'Support Ticket Triage — Daily',
    description: 'Daily automated triage of incoming support tickets: categorization, priority assignment, and routing to appropriate teams.',
    type: 'Support',
    status: 'running',
    currentStep: 2,
    totalSteps: 3,
    startedAt: '2026-03-30T10:00:00Z',
    updatedAt: '2026-03-30T10:30:00Z',
    humanBaseline: 4,
    grossValue: 150,
    totalTokens: 34500,
    agentTokens: 28900,
    hostTokens: 5600,
    humanInterventions: 0,
    agents: [
      { id: 'a8', name: 'Ticket Classifier', status: 'completed', description: 'Analyzes incoming support tickets using NLP to categorize by type (bug, feature, billing, inquiry) and product area. Trained on historical ticket data.' },
      { id: 'a9', name: 'Priority Engine', status: 'running', description: 'Evaluates tickets against SLA requirements, customer tier, and issue severity. Cross-references known outages and recent deployments for context.' },
      { id: 'a10', name: 'Router Agent', description: 'Routes prioritized tickets to the appropriate team based on category, required expertise, and current team capacity. Balances workload across teams.' },
    ],
    steps: [
      {
        id: 's11',
        name: 'Classify incoming tickets',
        status: 'completed',
        agentId: 'a8',
        agentName: 'Ticket Classifier',
        startedAt: '2026-03-30T10:00:00Z',
        completedAt: '2026-03-30T10:12:00Z',
        action: 'Analyzed 23 new support tickets. Categorized each by type (bug, feature request, billing, general inquiry) and product area.',
        delivered: '23 tickets classified: 8 bugs, 5 feature requests, 6 billing questions, 4 general inquiries. Classification confidence above 90% for all tickets.',
        reasoning: 'Accurate classification is essential for routing. Used historical ticket patterns and keyword analysis to categorize. High confidence across all tickets means no manual classification needed.',
      },
      {
        id: 's12',
        name: 'Assign priority levels',
        status: 'running',
        agentId: 'a9',
        agentName: 'Priority Engine',
        startedAt: '2026-03-30T10:12:00Z',
        completedAt: null,
        action: 'Evaluating each ticket against SLA requirements, customer tier, and issue severity. Cross-referencing with known outages and recent deployments.',
        delivered: null,
        reasoning: 'Priority assignment considers multiple factors: customer value (enterprise vs. free tier), issue impact (blocking vs. cosmetic), and SLA deadlines. Currently processing enterprise customer tickets first.',
      },
      {
        id: 's13',
        name: 'Route to teams',
        agentId: 'a10',
        agentName: 'Router Agent',
        startedAt: null,
        completedAt: null,
        action: null,
        delivered: null,
        reasoning: null,
      },
    ],
  },
  {
    id: 'wf-004',
    name: 'Contract Review — Vendor Agreement',
    description: 'Automated review of a new vendor contract for risk clauses, compliance issues, and terms comparison against company standards.',
    type: 'Legal',
    status: 'failed',
    currentStep: 2,
    totalSteps: 5,
    startedAt: '2026-03-30T08:30:00Z',
    updatedAt: '2026-03-30T09:05:00Z',
    humanBaseline: 12,
    grossValue: 500,
    totalTokens: 89300,
    agentTokens: 71200,
    hostTokens: 18100,
    humanInterventions: 1,
    agents: [
      { id: 'a11', name: 'Document Parser', status: 'completed', description: 'Extracts clauses, sections, and appendices from legal documents. Maps them to standard contract categories for structured analysis.' },
      { id: 'a12', name: 'Risk Analyzer', status: 'failed', description: 'Compares contract clauses against company risk thresholds and standard templates. Identifies non-standard, high-risk, or unfavorable terms.' },
      { id: 'a13', name: 'Compliance Checker', description: 'Verifies contract terms against regulatory requirements and industry standards. Checks for mandatory clauses and prohibited terms.' },
    ],
    steps: [
      {
        id: 's14',
        name: 'Parse contract document',
        status: 'completed',
        agentId: 'a11',
        agentName: 'Document Parser',
        startedAt: '2026-03-30T08:30:00Z',
        completedAt: '2026-03-30T08:45:00Z',
        action: 'Extracted all clauses, sections, and appendices from the 42-page vendor agreement PDF. Identified 127 distinct clauses across 8 sections.',
        delivered: 'Structured contract data with 127 clauses mapped to standard categories (liability, indemnification, termination, IP, confidentiality, payment, SLA, miscellaneous).',
        reasoning: 'Structured extraction enables clause-by-clause analysis against company standards. The 42-page length is above average, suggesting a complex agreement requiring thorough review.',
      },
      {
        id: 's15',
        name: 'Analyze risk clauses',
        status: 'failed',
        agentId: 'a12',
        agentName: 'Risk Analyzer',
        startedAt: '2026-03-30T08:45:00Z',
        completedAt: '2026-03-30T09:05:00Z',
        action: 'Attempted to compare liability and indemnification clauses against company risk thresholds. Encountered an unrecognized clause structure in Section 4.7 that could not be mapped to any standard category.',
        delivered: 'Partial analysis: 89 of 127 clauses analyzed. Section 4.7 contains a non-standard mutual liability clause with embedded arbitration terms that the agent could not parse.',
        reasoning: 'The non-standard clause structure in 4.7 combines liability limitation with binding arbitration in a way that doesn\'t match any template in the training data. Failing safely rather than misinterpreting a potentially high-risk clause.',
        activityLog: [
          { time: '08:45:05', type: 'thinking', text: 'I have 127 structured clauses from Document Parser. I need to compare each one against our company\'s risk thresholds. Sections 1-3 are usually boilerplate, but Section 4 (Liability & Indemnification) is where the real risk lives. I\'ll batch-process the safe sections first and spend more time on Section 4.' },
          { time: '08:46:30', type: 'tool', tool: 'load_templates', text: 'Loading company risk threshold templates', result: 'Loaded 3 templates:\n• liability_standard.json — max acceptable: $5M cap\n• indemnification_limits.json — mutual indemnification required\n• ip_protection_baseline.json — work product must transfer to client' },
          { time: '08:48:00', type: 'tool', tool: 'analyze_clauses', text: 'Batch analyzing Sections 1-3 (52 clauses) against templates', result: 'Section 1 (Definitions): 18 clauses — all standard ✓\nSection 2 (Scope of Work): 22 clauses — all standard ✓\nSection 3 (Payment Terms): 12 clauses — NET-45 terms (within policy) ✓\nFlags: 0' },
          { time: '08:51:20', type: 'thinking', text: 'Sections 1-3 are clean. Now for Section 4 — Liability & Indemnification. This is the section that most often contains problematic clauses. I\'ll go clause by clause here instead of batching.' },
          { time: '08:53:00', type: 'tool', tool: 'analyze_clauses', text: 'Analyzing clauses 4.1 through 4.6 individually', result: 'Clause 4.1 (Liability Cap): $2M mutual cap — within $5M threshold ✓\nClause 4.2 (Exclusions): Standard carve-outs for gross negligence ✓\nClause 4.3 (Indemnification): Mutual indemnification — matches template ✓\nClause 4.4 (Insurance Req): $1M minimum — standard ✓\nClause 4.5 (Warranty): 12-month warranty period — standard ✓\nClause 4.6 (Limitation): Consequential damages waived — standard ✓' },
          { time: '08:55:30', type: 'thinking', text: 'Clauses 4.1-4.6 are all within thresholds. But 4.7 is... unusual. Let me read it carefully.\n\nIt appears to combine a mutual liability limitation with embedded binding arbitration terms in the same clause. I\'ve never seen this structure before — normally these are separate clauses. The arbitration clause references back to the liability cap, but the reference is circular.' },
          { time: '08:57:00', type: 'tool', tool: 'analyze_clauses', text: 'Deep analysis of clause 4.7 — attempting to decompose structure', result: 'UNABLE TO CLASSIFY\nClause 4.7 structure is non-standard:\n• Contains mutual liability limitation language\n• Embeds binding arbitration terms within the limitation\n• Arbitration scope references "limitations set forth herein" (circular)\n• Ambiguous: could limit EITHER party\'s liability depending on interpretation\n• No matching template found in 847-clause training corpus\nConfidence: 23% (below 80% threshold)' },
          { time: '08:59:15', type: 'thinking', text: 'This is a problem. The circular reference between the liability cap and the arbitration scope means I genuinely cannot determine whose liability is being limited. If I interpret it wrong:\n\n• Scenario A: It limits THEIR liability → we\'re exposed to unlimited damages with mandatory arbitration\n• Scenario B: It limits OUR liability → favorable to us, but the arbitration terms may override it\n\nI am not confident enough to make this call. A wrong interpretation here could expose the company to significant financial risk. I need to fail safely and escalate this to human legal review.' },
          { time: '09:01:00', type: 'tool', tool: 'analyze_clauses', text: 'Batch analyzing Sections 5-8 (remaining 37 clauses)', result: 'Section 5 (IP Rights): 12 clauses — work product transfers to client ✓\nSection 6 (Confidentiality): 9 clauses — standard NDA terms ✓\nSection 7 (Term & Termination): 8 clauses — 30-day notice, standard ✓\nSection 8 (Miscellaneous): 8 clauses — standard boilerplate ✓\nFlags: 0' },
          { time: '09:03:30', type: 'thinking', text: 'Sections 5-8 are all clear. Final count: 89 of 127 clauses fully analyzed and within thresholds. The remaining 38 clauses in Section 4.7 and its sub-references cannot be safely interpreted.\n\nI\'m going to fail this step and escalate. It\'s better to flag one ambiguous clause for human review than to risk misinterpreting a potentially high-exposure liability provision.' },
          { time: '09:05:00', type: 'result', text: '⚠ FAILED — Escalating to human legal review\n\nSection 4.7 contains a non-standard clause combining mutual liability limitation with embedded binding arbitration. The circular reference structure makes it impossible to determine which party\'s liability is being limited.\n\nRisk: Potential unlimited liability exposure with mandatory arbitration.\n\nCompleted: 89/127 clauses analyzed (all clear)\nBlocked: Section 4.7 (38 dependent clauses)\nRecommendation: Human attorney should review Section 4.7 before this contract is signed.' },
        ],
      },
      {
        id: 's16',
        name: 'Check regulatory compliance',
        agentId: 'a13',
        agentName: 'Compliance Checker',
        startedAt: null,
        completedAt: null,
        action: null,
        delivered: null,
        reasoning: null,
      },
      {
        id: 's17',
        name: 'Compare against standards',
        agentId: 'a12',
        agentName: 'Risk Analyzer',
        startedAt: null,
        completedAt: null,
        action: null,
        delivered: null,
        reasoning: null,
      },
      {
        id: 's18',
        name: 'Generate review summary',
        agentId: 'a11',
        agentName: 'Document Parser',
        startedAt: null,
        completedAt: null,
        action: null,
        delivered: null,
        reasoning: null,
      },
    ],
  },
  {
    id: 'wf-005',
    name: 'Employee Offboarding — J. Martinez',
    description: 'Automated offboarding process including access revocation, equipment tracking, final payroll, and exit documentation.',
    type: 'HR',
    status: 'completed',
    currentStep: 5,
    totalSteps: 5,
    startedAt: '2026-03-29T14:00:00Z',
    updatedAt: '2026-03-29T15:45:00Z',
    humanBaseline: 6,
    grossValue: 240,
    totalTokens: 52100,
    agentTokens: 41200,
    hostTokens: 10900,
    humanInterventions: 0,
    agents: [
      { id: 'a14', name: 'Access Manager', status: 'completed', description: 'Disables Active Directory accounts, revokes SSO tokens across SaaS apps, removes shared drive access, and archives email to compliance vault.' },
      { id: 'a15', name: 'HR Processor', status: 'completed', description: 'Handles payroll calculations, document generation (separation agreements, COBRA, NDA), and HRIS updates for employee lifecycle events.' },
      { id: 'a16', name: 'IT Asset Tracker', status: 'completed', description: 'Tracks company equipment assignments, generates return checklists, sends shipping labels, and schedules follow-up reminders for unreturned assets.' },
    ],
    steps: [
      {
        id: 's19',
        name: 'Revoke system access',
        status: 'completed',
        agentId: 'a14',
        agentName: 'Access Manager',
        startedAt: '2026-03-29T14:00:00Z',
        completedAt: '2026-03-29T14:15:00Z',
        action: 'Disabled Active Directory account, revoked SSO tokens for 12 SaaS applications, removed from 3 shared drives, and archived email to compliance vault.',
        delivered: 'Access revocation report: all 16 access points disabled. Email archived (2.3 GB). Shared drive files preserved with ownership transferred to manager.',
        reasoning: 'Immediate access revocation is required by security policy within 1 hour of offboarding initiation. Email archival ensures compliance with 7-year retention requirement.',
      },
      {
        id: 's20',
        name: 'Process final payroll',
        status: 'completed',
        agentId: 'a15',
        agentName: 'HR Processor',
        startedAt: '2026-03-29T14:15:00Z',
        completedAt: '2026-03-29T14:35:00Z',
        action: 'Calculated final pay including 6 accrued PTO days, prorated March salary, and pending expense reimbursement of $847.',
        delivered: 'Final payroll packet: $4,923.47 total (salary: $3,461.00, PTO: $1,615.47, expenses: $847.00, deductions: -$1,000.00 for benefits).',
        reasoning: 'State law requires final pay within 72 hours. Including all components in one payment simplifies the process and ensures compliance.',
      },
      {
        id: 's21',
        name: 'Track equipment return',
        status: 'completed',
        agentId: 'a16',
        agentName: 'IT Asset Tracker',
        startedAt: '2026-03-29T14:35:00Z',
        completedAt: '2026-03-29T15:00:00Z',
        action: 'Generated equipment return checklist from asset database. Sent shipping label to employee. Marked 3 items as pending return: MacBook Pro, monitor, YubiKey.',
        delivered: 'Equipment return initiated: 3 assets tracked. Shipping label sent. Expected return by 2026-04-05. Reminder scheduled for 2026-04-03.',
        reasoning: 'Equipment tracking prevents asset loss. Automated shipping label reduces friction for the departing employee. Follow-up reminder ensures timely return.',
      },
      {
        id: 's22',
        name: 'Generate exit documents',
        status: 'completed',
        agentId: 'a15',
        agentName: 'HR Processor',
        startedAt: '2026-03-29T15:00:00Z',
        completedAt: '2026-03-29T15:25:00Z',
        action: 'Generated separation agreement, COBRA notification, final NDA acknowledgment, and exit interview summary from template library.',
        delivered: '4 documents generated and sent to employee and HR manager for signature. COBRA notification sent to benefits provider.',
        reasoning: 'Legal requires separation agreement and NDA acknowledgment for all departures. COBRA notification is federally mandated within 14 days.',
      },
      {
        id: 's23',
        name: 'Close offboarding case',
        status: 'completed',
        agentId: 'a15',
        agentName: 'HR Processor',
        startedAt: '2026-03-29T15:25:00Z',
        completedAt: '2026-03-29T15:45:00Z',
        action: 'Updated HRIS with termination date, archived employee record, notified relevant stakeholders, and closed the offboarding case.',
        delivered: 'Offboarding complete. HRIS updated. 5 stakeholders notified (direct manager, HR director, IT, finance, legal). Case closed.',
        reasoning: 'Centralizing the closure notification ensures all departments are informed simultaneously, preventing gaps in the offboarding process.',
      },
    ],
  },
  {
    id: 'wf-006',
    name: 'Data Quality Review — March Report',
    description: 'Automated data quality checks flagged anomalies in the March financial report requiring human verification before the report can be finalized and distributed.',
    type: 'Analytics',
    status: 'human_intervention',
    currentStep: 2,
    totalSteps: 4,
    startedAt: '2026-03-31T08:00:00Z',
    updatedAt: '2026-03-31T09:15:00Z',
    humanBaseline: 4,
    grossValue: 300,
    totalTokens: 42000,
    agentTokens: 28000,
    hostTokens: 14000,
    humanInterventions: 1,
    agents: [
      { id: 'a18', name: 'Data Validator', status: 'completed', description: 'Loads and validates source datasets, checks for schema conformance, null values, and referential integrity against expected business rules.' },
      { id: 'a19', name: 'Anomaly Detector', status: 'paused', description: 'Applies statistical models to detect outliers and anomalies in financial data. Generates confidence scores and flags items exceeding defined thresholds.' },
    ],
    steps: [
      {
        id: 's24',
        name: 'Load and validate dataset',
        status: 'completed',
        agentId: 'a18',
        agentName: 'Data Validator',
        startedAt: '2026-03-31T08:00:00Z',
        completedAt: '2026-03-31T08:22:00Z',
        action: 'Loaded March financial dataset (4,312 records) and validated schema, null values, and referential integrity against business rules.',
        delivered: 'Dataset validated: 4,312 records loaded. 3 schema warnings (non-critical). 0 null violations. Referential integrity: PASS.',
        reasoning: 'Schema validation must complete before anomaly detection to ensure the detection models receive clean, well-structured inputs.',
      },
      {
        id: 's25',
        name: 'Detect anomalies',
        status: 'completed',
        agentId: 'a19',
        agentName: 'Anomaly Detector',
        startedAt: '2026-03-31T08:22:00Z',
        completedAt: '2026-03-31T09:10:00Z',
        action: 'Applied IQR and Z-score statistical models to 4,312 records. Identified 7 outlier transactions exceeding the 3-sigma threshold.',
        delivered: '7 anomalies detected: 4 high-confidence (>95%), 3 medium-confidence (75–85%). Total flagged value: $142,380.',
        reasoning: 'Multiple high-confidence anomalies with significant dollar values require human verification before the report can be certified accurate.',
      },
      {
        id: 's26',
        name: 'Human review required',
        status: 'human_intervention',
        agentId: 'a19',
        agentName: 'Anomaly Detector',
        startedAt: '2026-03-31T09:10:00Z',
        completedAt: null,
        action: 'Awaiting human analyst review of 7 flagged anomalies before proceeding to report generation.',
        delivered: null,
        reasoning: 'The flagged transactions exceed the automated approval threshold. A human analyst must confirm or dismiss each anomaly before the final report is generated.',
      },
      {
        id: 's27',
        name: 'Generate final report',
        agentId: 'a18',
        agentName: 'Data Validator',
        startedAt: null,
        completedAt: null,
        action: 'Compile verified findings into the March data quality report and distribute to stakeholders.',
        delivered: null,
        reasoning: 'Report generation is blocked on human review completion.',
      },
    ],
  },
];

// ─── Procedural workflow generator ───────────────────────────────────────────

const WF_TYPES = [
  {
    type: 'Client Onboarding',
    agents: ['Document Collector', 'Compliance Checker', 'Account Provisioner', 'Welcome Agent'],
    steps: ['Collect documents', 'Verify compliance', 'Provision account', 'Configure integrations', 'Generate welcome package', 'Send onboarding email'],
    instances: ['Acme Inc', 'Globex Corp', 'Initech LLC', 'Umbrella Co', 'Hooli', 'Pied Piper', 'Dunder Mifflin', 'Vandelay Industries', 'Sterling Cooper', 'Bluth Company', 'Wernham Hogg', 'Prestige Worldwide', 'Nakatomi Corp', 'Cyberdyne Systems', 'Oscorp Industries', 'Stark Industries', 'Wayne Enterprises', 'LexCorp', 'Tyrell Corporation', 'Soylent Corp'],
  },
  {
    type: 'Invoice Processing',
    agents: ['Invoice Extractor', 'Validation Agent', 'Approval Router'],
    steps: ['Extract invoice data', 'Validate against PO', 'Route for approval', 'Schedule payment', 'Archive records'],
    instances: ['Q1 Batch', 'Q2 Batch', 'Q3 Batch', 'Q4 Batch', 'Jan Invoices', 'Feb Invoices', 'Mar Invoices', 'Apr Invoices', 'May Invoices', 'Jun Invoices', 'Jul Invoices', 'Aug Invoices', 'Sep Invoices', 'Oct Invoices', 'Nov Invoices', 'Dec Invoices', 'Mid-Year Batch', 'Year-End Batch', 'Sprint 1 Vendors', 'Sprint 2 Vendors'],
  },
  {
    type: 'Support Ticket Triage',
    agents: ['Ticket Classifier', 'Priority Engine', 'Router Agent'],
    steps: ['Classify tickets', 'Assign priorities', 'Route to teams', 'Notify assignees', 'Update SLA tracker'],
    instances: ['Daily Run', 'Monday Batch', 'Tuesday Batch', 'Wednesday Batch', 'Thursday Batch', 'Friday Batch', 'Week 1', 'Week 2', 'Week 3', 'Week 4', 'Morning Shift', 'Afternoon Shift', 'High Priority Queue', 'Enterprise Queue', 'Bug Backlog', 'Feature Backlog', 'Billing Queue', 'Escalations', 'Weekend Catchup', 'End-of-Sprint'],
  },
  {
    type: 'Contract Review',
    agents: ['Document Parser', 'Risk Analyzer', 'Compliance Checker'],
    steps: ['Parse document', 'Analyze risk clauses', 'Check compliance', 'Compare against standards', 'Generate summary'],
    instances: ['Vendor Agreement', 'SaaS License', 'MSA Amendment', 'NDA Renewal', 'Service Contract', 'Partnership Deal', 'Reseller Agreement', 'Consulting Contract', 'Subcontractor NDA', 'Annual Renewal', 'SOW — Phase 2', 'Enterprise Agreement', 'Data Processing Agreement', 'Cloud Services Contract', 'Staffing Agreement', 'IP Assignment', 'Licensing Deal', 'Distribution Agreement', 'White-Label Contract', 'Joint Venture MOU'],
  },
  {
    type: 'Employee Offboarding',
    agents: ['Access Revocation Agent', 'Asset Recovery Agent', 'HR Processor'],
    steps: ['Revoke system access', 'Collect equipment', 'Process final payroll', 'Archive employee record', 'Notify stakeholders'],
    instances: ['J. Smith', 'A. Johnson', 'M. Williams', 'S. Brown', 'C. Jones', 'D. Miller', 'R. Davis', 'P. Wilson', 'K. Taylor', 'L. Anderson', 'T. Thomas', 'B. Jackson', 'G. White', 'H. Harris', 'F. Martin', 'E. Garcia', 'N. Martinez', 'O. Robinson', 'I. Clark', 'Q. Rodriguez'],
  },
  {
    type: 'Data Quality Review',
    agents: ['Data Validator', 'Anomaly Detector'],
    steps: ['Load dataset', 'Detect anomalies', 'Human review', 'Generate report', 'Distribute findings'],
    instances: ['March Report', 'Q1 Summary', 'Q2 Summary', 'Q3 Summary', 'Q4 Summary', 'Annual Report', 'Sales Data', 'Finance Data', 'Customer Data', 'Product Metrics', 'Marketing Analytics', 'HR Headcount', 'Inventory Data', 'Compliance Report', 'Survey Results', 'Usage Analytics', 'Revenue Report', 'Cost Analysis', 'Forecast Model', 'Audit Data'],
  },
  {
    type: 'Compliance Audit',
    agents: ['Policy Checker', 'Evidence Collector', 'Report Generator'],
    steps: ['Gather evidence', 'Review policies', 'Identify gaps', 'Generate findings', 'Route for sign-off'],
    instances: ['SOC 2 Type II', 'ISO 27001', 'GDPR Review', 'HIPAA Assessment', 'PCI DSS Q1', 'PCI DSS Q2', 'PCI DSS Q3', 'PCI DSS Q4', 'Annual Security', 'Access Control Review', 'Data Retention Audit', 'Vendor Risk Q1', 'Vendor Risk Q2', 'Privacy Impact', 'Change Management', 'Incident Review', 'Patch Compliance', 'Endpoint Audit', 'Cloud Config Review', 'Third-Party Audit'],
  },
  {
    type: 'Payroll Processing',
    agents: ['Timesheet Validator', 'Tax Calculator', 'Payment Processor'],
    steps: ['Validate timesheets', 'Calculate taxes', 'Process payments', 'Generate statements', 'Archive records'],
    instances: ['Jan Payroll', 'Feb Payroll', 'Mar Payroll', 'Apr Payroll', 'May Payroll', 'Jun Payroll', 'Jul Payroll', 'Aug Payroll', 'Sep Payroll', 'Oct Payroll', 'Nov Payroll', 'Dec Payroll', 'Mid-Month Run', 'Bonus Run Q1', 'Bonus Run Q2', 'Bonus Run Q3', 'Bonus Run Q4', 'Contractor Payments', 'Commission Run', 'Year-End Adjustment'],
  },
  {
    type: 'IT Access Review',
    agents: ['Account Scanner', 'Permission Analyzer', 'Access Provisioner'],
    steps: ['Scan accounts', 'Analyze permissions', 'Flag anomalies', 'Update access', 'Generate report'],
    instances: ['Engineering Team', 'Product Team', 'Sales Team', 'Marketing Team', 'Finance Team', 'HR Team', 'Legal Team', 'Support Team', 'Executive Team', 'Contractors', 'New Hires Q1', 'New Hires Q2', 'New Hires Q3', 'New Hires Q4', 'Cloud Resources', 'Database Access', 'Admin Accounts', 'Service Accounts', 'Third-Party Access', 'Quarterly Review'],
  },
  {
    type: 'Vendor Onboarding',
    agents: ['Due Diligence Agent', 'Contract Setup Agent', 'System Integration Agent'],
    steps: ['Due diligence check', 'Setup contract', 'Configure integrations', 'Test connection', 'Activate vendor'],
    instances: ['Acme Supplies', 'Tech Partners Inc', 'DataStream Co', 'CloudFirst LLC', 'SecureNet', 'LogiServe', 'ProSolutions', 'NextGen Vendors', 'AlphaTech', 'BetaServices', 'CoreSuppliers', 'DeltaGroup', 'EpsilonWorks', 'FutureTech', 'GlobalSources', 'HorizonVendors', 'InnovateCo', 'JetStream', 'KeyVendors', 'LightSpeed'],
  },
  {
    type: 'Budget Reconciliation',
    agents: ['Transaction Classifier', 'Variance Analyzer', 'Report Builder'],
    steps: ['Import transactions', 'Classify expenses', 'Identify variances', 'Generate report', 'Route for approval'],
    instances: ['Q1 2025', 'Q2 2025', 'Q3 2025', 'Q4 2025', 'Jan 2026', 'Feb 2026', 'Mar 2026', 'Engineering Budget', 'Marketing Budget', 'Sales Budget', 'OpEx Review', 'CapEx Review', 'Travel Expenses', 'SaaS Spend', 'Headcount Budget', 'R&D Budget', 'Customer Success', 'Infrastructure Costs', 'Annual Closeout', 'Mid-Year Review'],
  },
  {
    type: 'Risk Assessment',
    agents: ['Risk Identifier', 'Impact Analyzer', 'Mitigation Planner'],
    steps: ['Identify risks', 'Analyze impact', 'Score likelihood', 'Plan mitigation', 'Generate report'],
    instances: ['Project Alpha', 'Project Beta', 'Project Gamma', 'Q1 Portfolio', 'Q2 Portfolio', 'Q3 Portfolio', 'Q4 Portfolio', 'Vendor Risk', 'Cyber Risk', 'Operational Risk', 'Financial Risk', 'Regulatory Risk', 'Market Risk', 'Supply Chain', 'Data Privacy', 'Infrastructure', 'Third-Party', 'Business Continuity', 'AI Systems', 'Cloud Migration'],
  },
];

export const WORKFLOW_TYPE_NAMES = WF_TYPES.map(t => t.type);

export const TYPE_COLORS = [
  '#6EFF95', '#60A5FA', '#FBBF24', '#FF6B6B', '#E8976F',
  '#A78BFA', '#34D399', '#F472B6', '#38BDF8', '#FB923C',
  '#4ADE80', '#C084FC',
];

export const getTypeColor = (typeName) =>
  TYPE_COLORS[WORKFLOW_TYPE_NAMES.indexOf(typeName) % TYPE_COLORS.length];

// Department chart line colors — distinct from TYPE_COLORS
// Index by position in accessibleDepartments array; Unassigned always uses UNASSIGNED_COLOR
export const DEPARTMENT_COLORS = ['#FF9F43', '#54A0FF', '#FF6B9D', '#FECA57', '#48DBFB'];
export const UNASSIGNED_COLOR = '#555566';

// Status slots: 44% completed, 26% running, 14% human_intervention, 12% pending, 4% failed
const STATUS_POOL = [
  ...Array(44).fill('completed'),
  ...Array(26).fill('running'),
  ...Array(14).fill('human_intervention'),
  ...Array(12).fill('pending'),
  ...Array(4).fill('failed'),
];

function generateWorkflows(count) {
  const now = Date.now();
  const yearAgo = now - 365 * 24 * 60 * 60 * 1000;
  const instanceCounters = {};
  WF_TYPES.forEach((t) => { instanceCounters[t.type] = 0; });

  const results = [];
  for (let i = 0; i < count; i++) {
    const wfType = WF_TYPES[i % WF_TYPES.length];
    const instanceIdx = instanceCounters[wfType.type] % wfType.instances.length;
    instanceCounters[wfType.type]++;

    const id = `wf-${String(i + 7).padStart(4, '0')}`;
    const statusIdx = ((i * 7) + (i * 13) % 11) % STATUS_POOL.length;
    const status = STATUS_POOL[statusIdx];
    const totalSteps = wfType.steps.length;

    const startedAtMs = yearAgo + ((i / count) * (now - yearAgo));
    const startedAt = new Date(startedAtMs).toISOString();

    let currentStep;
    let updatedAt;
    if (status === 'pending') {
      currentStep = 0;
      updatedAt = startedAt;
    } else if (status === 'completed') {
      currentStep = totalSteps;
      // Completed workflows: updatedAt is shortly after startedAt (they finished)
      updatedAt = new Date(startedAtMs + (1 + (i % 8)) * 3600000).toISOString();
    } else if (status === 'running') {
      currentStep = 1 + (i % (totalSteps - 1));
      updatedAt = new Date(startedAtMs + (i % 3) * 3600000).toISOString();
    } else if (status === 'human_intervention') {
      currentStep = 1 + (i % (totalSteps - 1));
      updatedAt = new Date(startedAtMs + (1 + (i % 5)) * 3600000).toISOString();
    } else {
      currentStep = 1 + (i % Math.ceil(totalSteps / 2));
      updatedAt = new Date(startedAtMs + (i % 4) * 3600000).toISOString();
    }

    const agents = wfType.agents.map((aName, aIdx) => {
      const agent = { id: `${id}-a${aIdx}`, name: aName };
      if (status !== 'pending') {
        if (aIdx < Math.floor(currentStep / (totalSteps / wfType.agents.length))) {
          agent.status = 'completed';
        } else if (aIdx === Math.floor(currentStep / (totalSteps / wfType.agents.length))) {
          agent.status = status === 'failed' ? 'failed' : 'running';
        }
      }
      return agent;
    });

    const steps = wfType.steps.map((sName, sIdx) => {
      const agentIdx = Math.min(sIdx, wfType.agents.length - 1);
      const step = {
        id: `${id}-s${sIdx}`,
        name: sName,
        agentId: `${id}-a${agentIdx}`,
        agentName: wfType.agents[agentIdx],
      };
      if (status !== 'pending') {
        if (sIdx < currentStep - 1) {
          step.status = 'completed';
          const stepStartMs = startedAtMs + sIdx * 720000;
          step.startedAt = new Date(stepStartMs).toISOString();
          step.completedAt = new Date(stepStartMs + 600000 + (i * 37 + sIdx * 13) % 600000).toISOString();
          step.action = `Processed ${sName.toLowerCase()} for ${wfType.instances[instanceIdx]}. Validated inputs, applied business rules, and produced required outputs.`;
          step.delivered = `${sName} completed successfully. All checks passed and outputs verified against acceptance criteria.`;
          step.reasoning = `This step is required by the ${wfType.type.toLowerCase()} workflow policy. Inputs from the previous step were validated before processing to ensure data integrity.`;
          step.activityLog = [
            { time: new Date(stepStartMs).toLocaleTimeString('en-US', { hour12: false, hour: '2-digit', minute: '2-digit', second: '2-digit' }), type: 'thinking', text: `Starting ${sName.toLowerCase()}. Let me review the inputs from the previous step and verify they meet the requirements before I proceed.` },
            { time: new Date(stepStartMs + 120000).toLocaleTimeString('en-US', { hour12: false, hour: '2-digit', minute: '2-digit', second: '2-digit' }), type: 'tool', tool: 'process_data', text: `Executing ${sName.toLowerCase()} for ${wfType.instances[instanceIdx]}`, result: `Processing complete. All business rules applied successfully.\nInputs validated: ${2 + (i + sIdx) % 4}\nOutputs generated: ${1 + (i + sIdx) % 3}\nWarnings: 0` },
            { time: new Date(stepStartMs + 360000).toLocaleTimeString('en-US', { hour12: false, hour: '2-digit', minute: '2-digit', second: '2-digit' }), type: 'thinking', text: `Results look good. All outputs match the expected format and pass validation. Moving to handoff.` },
            { time: new Date(stepStartMs + 540000).toLocaleTimeString('en-US', { hour12: false, hour: '2-digit', minute: '2-digit', second: '2-digit' }), type: 'result', text: `${sName} completed successfully for ${wfType.instances[instanceIdx]}. Ready for the next step.` },
          ];
        } else if (sIdx === currentStep - 1) {
          step.status = status === 'failed' ? 'failed'
            : status === 'human_intervention' ? 'human_intervention'
            : 'running';
          const stepStartMs = startedAtMs + sIdx * 720000;
          step.startedAt = new Date(stepStartMs).toISOString();
          if (status === 'failed') {
            step.completedAt = new Date(stepStartMs + 480000).toISOString();
            step.action = `Attempted to ${sName.toLowerCase()} but encountered an unrecoverable error during processing.`;
            step.delivered = `Partial results produced before failure. ${sName} could not be completed due to a validation error.`;
            step.reasoning = 'The input data contained an inconsistency that could not be resolved automatically. Failing safely to prevent downstream errors.';
            step.activityLog = [
              { time: new Date(stepStartMs).toLocaleTimeString('en-US', { hour12: false, hour: '2-digit', minute: '2-digit', second: '2-digit' }), type: 'thinking', text: `Starting ${sName.toLowerCase()}. Reviewing inputs from the previous step.` },
              { time: new Date(stepStartMs + 180000).toLocaleTimeString('en-US', { hour12: false, hour: '2-digit', minute: '2-digit', second: '2-digit' }), type: 'tool', tool: 'validate_input', text: `Validating inputs for ${sName.toLowerCase()}`, result: `ERROR: Validation failed.\nField: reference_id\nExpected: non-null string\nReceived: null\nThis is a required field and cannot be inferred.` },
              { time: new Date(stepStartMs + 300000).toLocaleTimeString('en-US', { hour12: false, hour: '2-digit', minute: '2-digit', second: '2-digit' }), type: 'thinking', text: 'The input is missing a required field. I cannot proceed safely without it — guessing would risk producing incorrect results. I need to fail this step and escalate.' },
              { time: new Date(stepStartMs + 480000).toLocaleTimeString('en-US', { hour12: false, hour: '2-digit', minute: '2-digit', second: '2-digit' }), type: 'result', text: `FAILED — ${sName} could not be completed. Missing required input field. Escalating for manual review.` },
            ];
          } else if (status === 'human_intervention') {
            step.completedAt = null;
            step.action = `Processed ${sName.toLowerCase()} and identified items requiring human judgment before the workflow can continue.`;
            step.delivered = null;
            step.reasoning = 'Automated analysis flagged items that exceed confidence thresholds for autonomous decision-making. Human review required before proceeding.';
            step.activityLog = [
              { time: new Date(stepStartMs).toLocaleTimeString('en-US', { hour12: false, hour: '2-digit', minute: '2-digit', second: '2-digit' }), type: 'thinking', text: `Starting ${sName.toLowerCase()}. Let me analyze the inputs and check against our thresholds.` },
              { time: new Date(stepStartMs + 240000).toLocaleTimeString('en-US', { hour12: false, hour: '2-digit', minute: '2-digit', second: '2-digit' }), type: 'tool', tool: 'analyze_data', text: `Running analysis for ${sName.toLowerCase()}`, result: `Analysis complete.\nItems processed: ${3 + (i + sIdx) % 5}\nItems within threshold: ${1 + (i + sIdx) % 3}\nItems requiring human review: ${1 + (i + sIdx) % 2}\nConfidence on flagged items: 62-74% (below 80% threshold)` },
              { time: new Date(stepStartMs + 420000).toLocaleTimeString('en-US', { hour12: false, hour: '2-digit', minute: '2-digit', second: '2-digit' }), type: 'thinking', text: 'Some items fall below my confidence threshold. I should not make autonomous decisions on these — the risk of error is too high. Pausing for human review.' },
            ];
          } else {
            step.completedAt = null;
            step.action = `Currently processing ${sName.toLowerCase()} for ${wfType.instances[instanceIdx]}.`;
            step.delivered = null;
            step.reasoning = `${sName} is in progress. Applying business rules and validating outputs before handoff to the next step.`;
            step.activityLog = [
              { time: new Date(stepStartMs).toLocaleTimeString('en-US', { hour12: false, hour: '2-digit', minute: '2-digit', second: '2-digit' }), type: 'thinking', text: `Starting ${sName.toLowerCase()}. Reviewing inputs and preparing to process.` },
              { time: new Date(stepStartMs + 180000).toLocaleTimeString('en-US', { hour12: false, hour: '2-digit', minute: '2-digit', second: '2-digit' }), type: 'tool', tool: 'process_data', text: `Processing ${sName.toLowerCase()} for ${wfType.instances[instanceIdx]}`, result: `In progress...\nRecords processed: ${10 + (i * 7 + sIdx * 3) % 40} of ${30 + (i * 11) % 50}\nEstimated completion: ${2 + (i + sIdx) % 5} minutes` },
            ];
          }
        } else {
          // Future steps — no data yet
          step.startedAt = null;
          step.completedAt = null;
          step.action = null;
          step.delivered = null;
          step.reasoning = null;
        }
      } else {
        // Pending workflow — no steps started
        step.startedAt = null;
        step.completedAt = null;
        step.action = null;
        step.delivered = null;
        step.reasoning = null;
      }
      return step;
    });

    const tokenBase = 20000 + (i % 50) * 2000;
    const humanBaselineHours = 2 + (i % WF_TYPES.length);
    const grossValue = humanBaselineHours * 40 + (i % 300);

    results.push({
      id,
      name: `${wfType.type} — ${wfType.instances[instanceIdx]}`,
      type: wfType.type,
      description: `Automated ${wfType.type.toLowerCase()} workflow for ${wfType.instances[instanceIdx]}.`,
      status,
      currentStep: Math.max(0, currentStep),
      totalSteps,
      startedAt,
      updatedAt,
      humanBaseline: humanBaselineHours,
      grossValue,
      totalTokens: tokenBase,
      agentTokens: Math.floor(tokenBase * 0.67),
      hostTokens: Math.floor(tokenBase * 0.33),
      humanInterventions: status === 'human_intervention' ? 1 : 0,
      agents,
      steps,
    });
  }
  return results;
}

export const workflows = [...seedWorkflows, ...generateWorkflows(1994)];

// Pre-computed flagged items (in a real app this would be derived from a flags collection)
export const initialFlaggedItems = [
  {
    workflowId: 'wf-001',
    stepId: 's2',
    workflowName: 'Client Onboarding — Acme Corp',
    stepName: 'Run compliance verification',
    agentName: 'Compliance Checker',
    issue: 'Verify insurance threshold logic',
    delivered: 'Compliance report generated with all 4 checks passing. Business license verified against California state registry, tax ID confirmed with IRS, insurance coverage validated at $1.4M (above $1M minimum).',
    reasoning: 'Compliance verification must be completed before account provisioning can begin. All checks passed automatically with no manual review required.',
    flaggedAt: '2026-03-30T10:15:00Z',
    status: 'completed',
  },
  {
    workflowId: 'wf-004',
    stepId: 's15',
    workflowName: 'Contract Review — Vendor Agreement',
    stepName: 'Analyze risk clauses',
    agentName: 'Risk Analyzer',
    issue: 'Unusual indemnification clause in 8.3',
    delivered: 'Extracted 12 key terms including payment schedule, liability caps, termination clauses, and IP ownership provisions.',
    reasoning: 'The indemnification clause in section 8.3 contains unusual language that shifts liability beyond standard thresholds. Flagged for legal review before proceeding.',
    flaggedAt: '2026-03-30T09:45:00Z',
    status: 'failed',
  },
];

// Model rate: $3 per 1M input tokens (simplified)
export const MODEL_RATE = 0.003 / 1000; // per token
export const HI_RATE = 50; // $ per human intervention

export function calculateEstCost(workflow) {
  return (workflow.totalTokens * MODEL_RATE) + (workflow.humanInterventions * HI_RATE);
}

export function calculateSpeedMultiplier(workflow) {
  const elapsedMs = new Date(workflow.updatedAt) - new Date(workflow.startedAt);
  const elapsedHours = elapsedMs / (1000 * 60 * 60);
  if (elapsedHours === 0) return 0;
  return workflow.humanBaseline / elapsedHours;
}

export function calculateNetSavings(workflow) {
  return workflow.grossValue - calculateEstCost(workflow);
}

export function formatDuration(ms) {
  const hours = Math.floor(ms / (1000 * 60 * 60));
  const minutes = Math.floor((ms % (1000 * 60 * 60)) / (1000 * 60));
  if (hours > 0) return `${hours}h ${minutes}m`;
  return `${minutes}m`;
}

export function formatTimestamp(iso) {
  return new Date(iso).toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  });
}

// ---------- User Profile ----------

export const defaultUserProfile = {
  id: 'u1',
  firstName: 'Kyle',
  lastName: 'Mitchell',
  email: 'kyle@caydigital.ai',
  role: 'Admin',
  description: '',
  avatarUrl: null,
};
